import SmsIcon from '@mui/icons-material/Sms';
import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { EEmployeeTalkStatus } from '@sharedInterfaces/IEmployeeTalk';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getEmployeeTalks from '@src/APIs/graphQl/EmployeeTalk/getEmployeeTalks';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface EmployeeTalksWidgetProps
{
    profilePageStyle?: boolean;
}

export default function EmployeeTalksWidget({ profilePageStyle = false }: EmployeeTalksWidgetProps = {})
{
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const currentUser = useSelector((state: AppState) => state.employee);
    const employeeTalks = useSelector((state: AppState) => state.employeeTalks);
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    useEffect(() =>
    {
        if (employeeTalks.length === 0)
        {
            getEmployeeTalks();
        }
    }, [employeeTalks.length]);

    const myEmployeeTalks = employeeTalks.filter((talk) => talk.employeeId === currentUser.id);

    const getStatusChipColor = (status: EEmployeeTalkStatus) =>
    {
        switch (status)
        {
            case EEmployeeTalkStatus.PLANNED:
                return 'primary';
            case EEmployeeTalkStatus.COMPLETED:
                return 'success';
            case EEmployeeTalkStatus.CANCELLED:
                return 'error';
            case EEmployeeTalkStatus.POSTPONED:
                return 'warning';
            default:
                return 'default';
        }
    };

    const getEmployeeName = (employeeId: number) =>
    {
        const employee = allEmployees.find((e) => e.id === employeeId);
        return employee ? employee.title : `ID: ${employeeId}`;
    };

    const renderEmployeePicture = (employeeId: number) =>
    {
        const employee = allEmployees.find((e) => e.id === employeeId);
        if (!employee) return null;

        const parts = (employee.title ?? '').trim().split(' ');
        const firstName = parts[0] || '';
        const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

        return (
            <EmployeePicture
                employee={{
                    id: employee.id,
                    firstName,
                    lastName,
                    pictureId: employee.pictureId || null,
                }}
                avatarSize={32}
            />
        );
    };

    const formatDate = (dateString?: string) =>
    {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const upcomingTalks = myEmployeeTalks.filter((talk) => talk.status2 === EEmployeeTalkStatus.PLANNED);
    const recentTalks = myEmployeeTalks.slice(0, 4);

    const handleViewAll = () =>
    {
        navigate(`/${ELinks.EMPLOYEE_TALKS}`);
    };

    return (
        <BaseWidget
            elevation={1}
            sx={
                profilePageStyle
                    ? {
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}05 100%)`,
                        border: `1px solid ${theme.palette.primary.main}20`,
                    }
                    : {}
            }
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 63, sm: 63 },
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: profilePageStyle ? 'transparent' : theme.palette.background.paper,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmsIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {langStrings.employeeTalks}
                    </Typography>
                    {upcomingTalks.length > 0 && (
                        <Chip label={`${upcomingTalks.length} upcoming`} color="primary" size="small" sx={{ ml: 1 }} />
                    )}
                </Box>
            </Box>
            <Box sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                    {recentTalks.length > 0 ? (
                        recentTalks.map((talk) => (
                            <Box
                                key={talk.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                                onClick={() => navigate(`/${ELinks.EMPLOYEE_TALKS}/${talk.id}`)}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                    {renderEmployeePicture(talk.employeeId)}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {talk.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {getEmployeeName(talk.employeeId)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(talk.scheduledDate)}
                                    </Typography>
                                    <Chip
                                        label={talk.status2}
                                        color={getStatusChipColor(talk.status2)}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>
                        ))
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {langStrings.noEmployeeTalksFound}
                            </Typography>
                        </Box>
                    )}
                </Stack>

                <Button fullWidth variant="outlined" onClick={handleViewAll} sx={{ mt: 2 }}>
                    {langStrings.oftenUsed.viewAll}
                </Button>
            </Box>
        </BaseWidget>
    );
}
